import React from "react";
import Badge from "../../Badge/Badge";
import dayjs from "dayjs";
import { getOrderStatus } from "./orders.helper";
import {
  formatMoney,
  formatTime,
} from "../../../utils/helperFunctions";
import {
  getMobileOperatingSystem,
  ingestData,
  isMobile,
  navigateToURL,
  triggerNavigation,
} from "../../../bridges/BridgeServices";
import { getPlatformForBot } from "../../Chatbot/ChatbotUtil";
import {
  CHATBOT_SOURCE,
  EVENTS,
  RECENT_ACTIVITY_TYPES,
} from "../../../utils/constants";
import { getUrlConfig } from "../../../bridges/deeplinkUrlConfig";
import { StockIcon } from "../../StockIcon/StockIcon";
import "./orders.scss";
import fetchOrders from "../../../services/RecentActivity/recentOrders/recentOrders.service";

export const InstrumentType = {
  FUTURE: "FUTURE",
  OPTION: "OPTION",
  EQUITY: "EQUITY",
  INDEX: "INDEX",
  ETF: "ETF",
};

export const OPTION_TYPE_NAME_MAP = {
  C: "CALL",
  P: "PUT",
  CALL: "CALL",
  PUT: "PUT",
};

export const StatusBadgeVariant = {
  FAILED: "FAILED",
  SUCCESS: "SUCCESS",
  CANCEL: "CANCEL",
  PENDING: "PENDING",
};

export const ORDER_UI_STATUS = {
  PLACED: "Placed",
  FILLING: "Filling",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
  SUCCESSFUL: "Successful",
  FAILED: "Failed",
  MODIFYING: "Modifying",
  CANCELLING: "Cancelling",
  PARTIAL_SUCCESSFULL: "Partial",
};

export const ORDER_STATUS_CLASSNAMES = {
  [ORDER_UI_STATUS.PLACED]: "success",
  [ORDER_UI_STATUS.FILLING]: "pending",
  [ORDER_UI_STATUS.REJECTED]: "failed",
  [ORDER_UI_STATUS.CANCELLED]: "failed",
  [ORDER_UI_STATUS.SUCCESSFUL]: "success",
  [ORDER_UI_STATUS.FAILED]: "failed",
  [ORDER_UI_STATUS.MODIFYING]: "pending",
  [ORDER_UI_STATUS.CANCELLING]: "failed",
  [ORDER_UI_STATUS.PARTIAL_SUCCESSFULL]: "pending",
};

export const ORDER_API_STATUS = {
  // pending
  ACCEPTED: "ACCEPTED",
  PLACED: "PLACED",
  MODIFY_ACCEPTED: "MODIFY_ACCEPTED",
  MODIFY_PLACED: "MODIFY_PLACED",
  CANCEL_ACCEPTED: "CANCEL_ACCEPTED",
  CANCEL_PLACED: "CANCEL_PLACED",

  // closed
  REJECTED: "REJECTED",
  CANCELLED: "CANCELLED",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
};

export const ORDER_TYPES = {
  MARKET: "Market order",
  LIMIT: "Limit order",
  CONDITIONAL_MARKET: "Conditional market order",
  CONDITIONAL_LIMIT: "Conditional limit order",
  GROUP_ORDER_MARKET: "Market + TP/SL",
  GROUP_ORDER_LIMIT: "Limit + TP/SL",
  GTT: "GTT",
};

export const ORDER_TYPE_HEADER_TEXT_MAP = {
  [ORDER_TYPES.CONDITIONAL_LIMIT]: "Conditional limit",
  [ORDER_TYPES.CONDITIONAL_MARKET]: "Conditional market",
  [ORDER_TYPES.MARKET]: "Market",
  [ORDER_TYPES.LIMIT]: "Limit",
  [ORDER_TYPES.GTT]: "GTT",
  [ORDER_TYPES.GROUP_ORDER_LIMIT]: "Limit + TP/SL",
  [ORDER_TYPES.GROUP_ORDER_MARKET]: "Market + TP/SL",
};

export const ORDER_STATUS_TO_UI_MAPPING = {
  [ORDER_API_STATUS.ACCEPTED]: ORDER_UI_STATUS.PLACED,
  [ORDER_API_STATUS.PLACED]: ORDER_UI_STATUS.PLACED,
  [ORDER_API_STATUS.REJECTED]: ORDER_UI_STATUS.REJECTED,
  [ORDER_API_STATUS.CANCELLED]: ORDER_UI_STATUS.CANCELLED,
  [ORDER_API_STATUS.COMPLETED]: ORDER_UI_STATUS.SUCCESSFUL,
  [ORDER_API_STATUS.FAILED]: ORDER_UI_STATUS.FAILED,
  [ORDER_API_STATUS.MODIFY_ACCEPTED]:
    ORDER_UI_STATUS.MODIFYING,
  [ORDER_API_STATUS.MODIFY_PLACED]:
    ORDER_UI_STATUS.MODIFYING,
  [ORDER_API_STATUS.CANCEL_ACCEPTED]:
    ORDER_UI_STATUS.CANCELLING,
  [ORDER_API_STATUS.CANCEL_PLACED]:
    ORDER_UI_STATUS.CANCELLING,
};

export const API_VS_UI_PRODUCT_TYPES_MAPPING = {
  MIS: "Intraday",
  CNC: "Delivery",
  NRML: "Overnight",
};

const StatusBadgeVariantMap = {
  [ORDER_UI_STATUS.PLACED]: StatusBadgeVariant.SUCCESS,
  [ORDER_UI_STATUS.FILLING]: StatusBadgeVariant.PENDING,
  [ORDER_UI_STATUS.REJECTED]: StatusBadgeVariant.FAILED,
  [ORDER_UI_STATUS.CANCELLED]: StatusBadgeVariant.CANCEL,
  [ORDER_UI_STATUS.SUCCESSFUL]: StatusBadgeVariant.SUCCESS,
  [ORDER_UI_STATUS.FAILED]: StatusBadgeVariant.FAILED,
  [ORDER_UI_STATUS.MODIFYING]: StatusBadgeVariant.PENDING,
  [ORDER_UI_STATUS.CANCELLING]: StatusBadgeVariant.PENDING,
  [ORDER_UI_STATUS.PARTIAL_SUCCESSFULL]:
    StatusBadgeVariant.PENDING,
};

const StatusClassMap = {
  [StatusBadgeVariant.SUCCESS]: "successVariant",
  [StatusBadgeVariant.FAILED]: "failedVariant",
  [StatusBadgeVariant.CANCEL]: "cancelVariant",
  [StatusBadgeVariant.PENDING]: "pendingVariant",
};

const EXPIRY_DATE_FORMAT = "DD MMM";

const getInstrumentName = instrumentDetails => {
  let instrumentName = instrumentDetails?.name;
  const isFno =
    instrumentDetails.instrumentType ===
      InstrumentType.FUTURE ||
    instrumentDetails.instrumentType ===
      InstrumentType.OPTION;

  if (!isFno) {
    return instrumentName;
  }
  const {
    instrumentType,
    symbol,
    optionsType,
    expiry,
    strikePrice,
  } = instrumentDetails;

  const expiryDate =
    expiry?.timestamp &&
    formatTime(expiry?.timestamp, EXPIRY_DATE_FORMAT);

  const optionType =
    optionsType && OPTION_TYPE_NAME_MAP[optionsType];

  const price = strikePrice ? strikePrice / 100 : "--";

  if (instrumentType === InstrumentType.FUTURE)
    instrumentName = `${symbol} ${expiryDate || ""} Future`;
  else if (instrumentType === InstrumentType.OPTION)
    instrumentName = `${symbol} ${
      expiryDate || ""
    } ${price} ${optionType || ""}`;

  return instrumentName;
};

export const API_ORDER_TYPES = {
  REGULAR: "REGULAR",
  TRIGGER: "TRIGGER",
  COVER: "COVER",
  BP_SL: "BP_SL",
};

export const API_PRICE_TYPES = {
  MARKET: "MARKET",
  LIMIT: "LIMIT",
};

const REGULAR_ORDER_MAP = {
  MARKET: ORDER_TYPES.MARKET,
  LIMIT: ORDER_TYPES.LIMIT,
};

const CONDITIONAL_ORDER_MAP = {
  MARKET: ORDER_TYPES.CONDITIONAL_MARKET,
  LIMIT: ORDER_TYPES.CONDITIONAL_LIMIT,
};

const GROUP_ORDER_MAP = {
  MARKET: ORDER_TYPES.GROUP_ORDER_MARKET,
  LIMIT: ORDER_TYPES.GROUP_ORDER_LIMIT,
};

export const getOrderType = (orderType, priceType) => {
  switch (orderType) {
    case "REGULAR":
      return REGULAR_ORDER_MAP[priceType];
    case "TRIGGER":
      return CONDITIONAL_ORDER_MAP[priceType];
    case "COVER":
    case "BP_SL":
      return GROUP_ORDER_MAP[priceType];
  }
};

const getPurchaseTextOrderType = (orderType, priceType) => {
  if (orderType === API_ORDER_TYPES.REGULAR) {
    if (priceType === API_PRICE_TYPES.MARKET)
      return ORDER_TYPES.MARKET;
    if (priceType === API_PRICE_TYPES.LIMIT)
      return ORDER_TYPES.LIMIT;
  }
  if (orderType === API_ORDER_TYPES.TRIGGER) {
    if (priceType === API_PRICE_TYPES.MARKET)
      return ORDER_TYPES.CONDITIONAL_MARKET;
    if (priceType === API_PRICE_TYPES.LIMIT)
      return ORDER_TYPES.CONDITIONAL_LIMIT;
  }
  return ORDER_TYPES.MARKET;
};

export const getPurchaseText = (
  orderType,
  priceType,
  limitPrice,
  triggerPrice
) => {
  let ordType = getPurchaseTextOrderType(
    orderType,
    priceType
  );
  switch (ordType) {
    case ORDER_TYPES.MARKET: {
      return "Market";
    }
    case ORDER_TYPES.LIMIT: {
      return `Limit ${formatMoney(limitPrice)}`;
    }
    case ORDER_TYPES.CONDITIONAL_MARKET: {
      return `Market | Trigger ${formatMoney(
        triggerPrice
      )}`;
    }
    case ORDER_TYPES.CONDITIONAL_LIMIT: {
      return `Limit ${formatMoney(
        limitPrice
      )} | Trigger ${formatMoney(triggerPrice)}`;
    }
    default:
      return "";
  }
};

export const orderTableConfig = {
  tableConfig: [
    {
      key: "name",
      header: "Name",
      accessor: (data, rowData) => {
        return (
          <div className="stockName">
            <StockIcon
              ppRefId={rowData.instrumentDetails.ppRefId}
            />
            <p>
              {getInstrumentName(rowData.instrumentDetails)}
            </p>
          </div>
        );
      },
    },
    {
      key: "transactionType",
      header: "Side",
      accessor: (data, rowData) => {
        return (
          <Badge
            className={`sidesBadge ${
              data === "BUY"
                ? "buyVariant"
                : "successVariant"
            }`}
            textClassName=""
            text={data}
          />
        );
      },
    },
    {
      key: "createdAt",
      header: "Date and Time",
      accessor: (data, rowData) => {
        return (
          <p>{dayjs(data).format("DD MMM hh:mm A")}</p>
        );
      },
    },
    {
      key: "productType",
      header: "Product",
      accessor: (data, rowData) => {
        return (
          <p>{API_VS_UI_PRODUCT_TYPES_MAPPING[data]}</p>
        );
      },
    },
    {
      key: "orderType",
      header: "Order type",
      accessor: (data, rowData) => {
        return (
          <p>
            {getOrderType(
              rowData.orderType,
              rowData.priceType
            )}
          </p>
        );
      },
    },
    {
      key: "quantity",
      header: "Quantity",
      align: "right",
      accessor: (data, rowData) => {
        return <p>{data}</p>;
      },
    },
    {
      key: "limitPrice",
      header: "Price",
      align: "right",
      accessor: (data, rowData) => {
        if (rowData.children) return null;
        const triggerPrice =
          rowData?.triggerPrice?.value > 0
            ? rowData?.triggerPrice?.value
            : undefined;
        return (
          <span>
            {` ${
              rowData?.limitPrice
                ? formatMoney(rowData?.limitPrice, {
                    withSign: rowData?.limitPrice < 0,
                  })
                : "Market"
            }`}
          </span>
        );
      },
    },
    {
      key: "orderStatus",
      header: "Status",
      accessor: (data, rowData) => {
        const orderStatusTitle =
          StatusBadgeVariantMap?.[getOrderStatus(rowData)];
        return (
          <Badge
            className={`statusBadge ${StatusClassMap[orderStatusTitle]}`}
            textClassName=""
            text={ORDER_STATUS_TO_UI_MAPPING[data]}
          />
        );
      },
    },
  ],
  onRowClick: data => {
    ingestData({
      name: EVENTS.HELP_ACTIVITY_POPUP_CLICKED,
      data: {
        source: isMobile()
          ? getMobileOperatingSystem()
          : "WEB",
        type: RECENT_ACTIVITY_TYPES.ORDERS,
        txnId: data?.orderId,
      },
    });
    if (isMobile()) {
      navigateToURL("CHATBOT", {
        src: CHATBOT_SOURCE.ORDERS,
        txnId: data.orderId,
        txnState: data.orderState,
        errorCode: data?.rejectionInfo?.rejectionCode,
        platform: getPlatformForBot(),
      });
    } else {
      const config = getUrlConfig({
        src: CHATBOT_SOURCE.ORDERS,
        txnId: data.orderId,
        txnState: data.orderStatus,
        errorCode: data?.rejectionInfo?.rejectionCode,
        platform: getPlatformForBot(),
      })?.["RECENT_ACTIVITY"];
      const orderHelpUrl = config.webURL;
      triggerNavigation({
        deepLink: "",
        webURL: orderHelpUrl,
      });
    }
  },
  triggerEndOfPage: data => {
    fetchOrders(data);
  },
};
